<template>
  <div class="platform-management">
    <!-- 顶部标题区域 -->
    <div class="page-header with-bg">
      <div class="header-content">
        <h1 class="page-title">
          <el-icon class="title-icon">
            <Platform />
          </el-icon>
          平台管理
        </h1>
        <p class="page-desc">统一管理各平台的配置信息</p>
      </div>
      <el-button type="primary" @click="fetchAllSettings" :loading="loading.refresh">
        <el-icon>
          <Refresh />
        </el-icon>
        刷新数据
      </el-button>
    </div>

    <!-- 标签页导航 -->
    <el-tabs v-model="activeTab" type="border-card" class="platform-tabs">

      <!-- 小程序 -->
      <el-tab-pane label="小程序" name="miniprogram">
        <el-card shadow="never" class="tab-card">
          <template #header>
            <div class="card-header">
              <div class="platform-info">
                <el-icon class="platform-icon mini-icon">
                  <Iphone />
                </el-icon>
                <span class="platform-name">小程序管理</span>
                <el-tag v-if="miniprogramForm.appid" type="success" size="small">已配置</el-tag>
                <el-tag v-else type="info" size="small">未配置</el-tag>
                <el-tag v-if="miniprogramForm.qrcode_filename" type="warning" size="small">
                  <el-icon>
                    <Picture />
                  </el-icon>
                  有二维码
                </el-tag>
              </div>
              <el-button type="primary" @click="saveMiniprogram" :loading="loading.miniprogram">
                <el-icon>
                  <Check />
                </el-icon>
                保存设置
              </el-button>
            </div>
          </template>

          <div class="form-container">
            <el-form :model="miniprogramForm" label-width="120px">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="小程序AppID">
                    <el-input v-model="miniprogramForm.appid" placeholder="请输入小程序AppID" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="小程序密钥">
                    <el-input v-model="miniprogramForm.secret" placeholder="请输入小程序密钥" show-password
                      autocomplete="new-password" />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="上传密钥">
                <el-input v-model="miniprogramForm.private_key" placeholder="请输入小程序上传密钥" type="textarea" :rows="3" />
                <div class="field-tip">
                  <el-icon>
                    <InfoFilled />
                  </el-icon>
                  上传密钥用于代码上传，请妥善保管
                </div>
              </el-form-item>

              <el-form-item label="访问地址">
                <el-input v-model="accessUrls.miniprogram" readonly>
                  <template #append>
                    <el-button @click="copyToClipboard(accessUrls.miniprogram)">
                      <el-icon>
                        <DocumentCopy />
                      </el-icon>
                    </el-button>
                  </template>
                </el-input>
                <div class="field-tip">此路径为小程序访问路径，需在小程序代码中配置</div>
              </el-form-item>

              <el-form-item label="手机号授权">
                <el-switch v-model="miniprogramForm.enable_phone_auth" :active-value="1" :inactive-value="0" />
                <span class="switch-label">{{ miniprogramForm.enable_phone_auth ? '已开启' : '已关闭' }}</span>
              </el-form-item>

              <!-- 小程序码显示区域 -->
              <el-form-item v-if="miniprogramForm.qrcode_url" label="小程序码">
                <div class="qrcode-container">
                  <div class="qrcode-image-wrapper">
                    <img :src="miniprogramForm.qrcode_url" alt="小程序码" class="qrcode-image" @error="handleQrcodeError" />
                  </div>

                  <div class="qrcode-info">
                    <div class="qrcode-meta">
                      <div class="meta-item" v-if="miniprogramForm.qrcode_last_generate_time">
                        <el-icon>
                          <Clock />
                        </el-icon>
                        <span>生成时间: {{ formatTime(miniprogramForm.qrcode_last_generate_time) }}</span>
                      </div>
                      <div class="meta-item" v-if="miniprogramForm.qrcode_generate_count">
                        <el-icon>
                          <DataAnalysis />
                        </el-icon>
                        <span>生成次数: {{ miniprogramForm.qrcode_generate_count }}</span>
                      </div>
                      <div class="meta-item">
                        <el-icon>
                          <SuccessFilled />
                        </el-icon>
                        <span>状态: 永久有效</span>
                      </div>
                    </div>

                    <p class="qrcode-tip">扫描二维码进入小程序</p>

                    <div class="qrcode-actions">
                      <el-button size="small" @click="downloadQrcode">
                        <el-icon>
                          <Download />
                        </el-icon>
                        下载
                      </el-button>
                      <el-button size="small" @click="copyToClipboard(miniprogramForm.qrcode_url)">
                        <el-icon>
                          <DocumentCopy />
                        </el-icon>
                        复制链接
                      </el-button>
                      <el-button size="small" @click="viewQrcode" type="primary">
                        <el-icon>
                          <View />
                        </el-icon>
                        查看大图
                      </el-button>
                    </div>
                  </div>
                </div>
              </el-form-item>

              <el-form-item label="操作">
                <div style="display: flex; gap: 10px; flex-wrap: wrap;">
                  <!-- 获取小程序码按钮 -->
                  <el-button type="primary" @click="getMiniprogramQrcode(false)"
                    :disabled="!miniprogramForm.appid || !miniprogramForm.secret" :loading="loading.miniQrcode">
                    <el-icon>
                      <Picture />
                    </el-icon>
                    {{ miniprogramForm.qrcode_url ? '重新获取' : '获取小程序码' }}
                  </el-button>

                  <!-- 强制重新生成按钮 -->
                  <el-button type="warning" @click="getMiniprogramQrcode(true)"
                    :disabled="!miniprogramForm.appid || !miniprogramForm.secret" :loading="loading.forceGenerate"
                    v-if="miniprogramForm.qrcode_url">
                    <el-icon>
                      <Refresh />
                    </el-icon>
                    强制重新生成
                  </el-button>

                  <!-- 上传小程序按钮 -->
                  <el-button type="success" @click="uploadMiniprogram"
                    :disabled="!miniprogramForm.appid || !miniprogramForm.private_key" :loading="loading.upload">
                    <el-icon>
                      <Upload />
                    </el-icon>
                    {{ loading.upload ? '上传中...' : '上传小程序' }}
                  </el-button>
                </div>
              </el-form-item>

              <el-form-item v-if="miniprogramForm.last_update_time" label="最后更新">
                <div class="update-info">
                  <el-icon>
                    <Clock />
                  </el-icon>
                  {{ miniprogramForm.last_update_time }}
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </el-tab-pane>


      <!-- 公众号 -->
      <el-tab-pane label="公众号" name="offiaccount">
        <el-card shadow="never" class="tab-card">
          <template #header>
            <div class="card-header">
              <div class="platform-info">
                <el-icon class="platform-icon offi-icon">
                  <ChatDotRound />
                </el-icon>
                <span class="platform-name">公众号管理</span>
                <el-tag v-if="offiaccountForm.appid" type="success" size="small">已配置</el-tag>
                <el-tag v-else type="info" size="small">未配置</el-tag>
              </div>
              <el-button type="primary" @click="saveOffiaccount" :loading="loading.offiaccount">
                <el-icon>
                  <Check />
                </el-icon>
                保存设置
              </el-button>
            </div>
          </template>

          <div class="form-container">
            <el-form :model="offiaccountForm" label-width="120px">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="公众号AppID">
                    <el-input v-model="offiaccountForm.appid" placeholder="请输入公众号AppID" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="公众号密钥">
                    <el-input v-model="offiaccountForm.secret" placeholder="请输入公众号密钥" show-password
                      autocomplete="new-password" />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="回调链接">
                <el-input v-model="offiaccountForm.callback_url" placeholder="请输入回调链接" />
              </el-form-item>

              <el-form-item label="访问地址">
                <el-input v-model="accessUrls.offiaccount" readonly>
                  <template #append>
                    <el-button @click="copyToClipboard(accessUrls.offiaccount)">
                      <el-icon>
                        <DocumentCopy />
                      </el-icon>
                    </el-button>
                  </template>
                </el-input>
              </el-form-item>

              <el-form-item label="手机号授权">
                <el-switch v-model="offiaccountForm.enable_phone_auth" :active-value="1" :inactive-value="0" />
                <span class="switch-label">{{ offiaccountForm.enable_phone_auth ? '已开启' : '已关闭' }}</span>
              </el-form-item>

              <el-form-item label="操作">
                <el-button type="warning" @click="generateOffiaccountQrcode" :disabled="!offiaccountForm.appid">
                  <el-icon>
                    <Picture />
                  </el-icon>
                  生成二维码
                </el-button>
              </el-form-item>

              <el-form-item v-if="offiaccountQrcode" label="公众号二维码">
                <div class="qrcode-container">
                  <img :src="offiaccountQrcode" alt="公众号二维码" class="qrcode-image" />
                  <p class="qrcode-tip">扫描二维码访问公众号</p>
                </div>
              </el-form-item>

              <el-form-item v-if="offiaccountForm.last_update_time" label="最后更新">
                <div class="update-info">
                  <el-icon>
                    <Clock />
                  </el-icon>
                  {{ offiaccountForm.last_update_time }}
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </el-tab-pane>

      <!-- H5平台 -->
      <el-tab-pane label="H5平台" name="h5">
        <el-card shadow="never" class="tab-card">
          <template #header>
            <div class="card-header">
              <div class="platform-info">
                <el-icon class="platform-icon h5-icon">
                  <Monitor />
                </el-icon>
                <span class="platform-name">H5管理</span>
                <el-tag type="info" size="small">基础配置</el-tag>
              </div>
              <el-button type="primary" @click="saveH5" :loading="loading.h5">
                <el-icon>
                  <Check />
                </el-icon>
                保存设置
              </el-button>
            </div>
          </template>

          <div class="form-container">
            <el-form :model="h5Form" label-width="120px">
              <el-form-item label="访问地址">
                <el-input v-model="accessUrls.h5" readonly>
                  <template #append>
                    <el-button @click="copyToClipboard(accessUrls.h5)">
                      <el-icon>
                        <DocumentCopy />
                      </el-icon>
                    </el-button>
                  </template>
                </el-input>
                <div class="field-tip">此地址为您的H5页面访问链接</div>
              </el-form-item>

              <el-form-item label="手机号授权">
                <el-switch v-model="h5Form.enable_phone_auth" :active-value="1" :inactive-value="0" />
                <span class="switch-label">{{ h5Form.enable_phone_auth ? '已开启' : '已关闭' }}</span>
              </el-form-item>

              <el-form-item v-if="h5Form.last_update_time" label="最后更新">
                <div class="update-info">
                  <el-icon>
                    <Clock />
                  </el-icon>
                  {{ h5Form.last_update_time }}
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </el-tab-pane>

      <!-- PC平台 -->
      <el-tab-pane label="PC平台" name="pc">
        <el-card shadow="never" class="tab-card">
          <template #header>
            <div class="card-header">
              <div class="platform-info">
                <el-icon class="platform-icon pc-icon">
                  <Monitor />
                </el-icon>
                <span class="platform-name">PC管理</span>
                <el-tag type="info" size="small">基础配置</el-tag>
              </div>
              <div class="card-actions">
                <el-button type="warning" @click="openWebsiteSettings">
                  <el-icon>
                    <Setting />
                  </el-icon>
                  网站设置
                </el-button>
                <el-button type="primary" @click="savePC" :loading="loading.pc">
                  <el-icon>
                    <Check />
                  </el-icon>
                  保存设置
                </el-button>
              </div>
            </div>
          </template>

          <div class="form-container">
            <el-form :model="pcForm" label-width="120px">
              <el-form-item label="访问地址">
                <el-input v-model="accessUrls.pc" readonly>
                  <template #append>
                    <el-button @click="copyToClipboard(accessUrls.pc)">
                      <el-icon>
                        <DocumentCopy />
                      </el-icon>
                    </el-button>
                  </template>
                </el-input>
                <div class="field-tip">此地址为您的PC网站访问链接</div>
              </el-form-item>

              <el-form-item label="手机号授权">
                <el-switch v-model="pcForm.enable_phone_auth" :active-value="1" :inactive-value="0" />
                <span class="switch-label">{{ pcForm.enable_phone_auth ? '已开启' : '已关闭' }}</span>
              </el-form-item>

              <el-form-item v-if="pcForm.last_update_time" label="最后更新">
                <div class="update-info">
                  <el-icon>
                    <Clock />
                  </el-icon>
                  {{ pcForm.last_update_time }}
                </div>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 网站设置弹窗 -->
    <el-dialog v-model="websiteDialogVisible" title="PC网站设置" width="800px" :lock-scroll="false"
      :close-on-click-modal="false">
      <el-form :model="websiteForm" label-width="120px" ref="websiteFormRef">
        <!-- 图片上传部分 -->
        <el-form-item label="PC端LOGO">
          <div class="avatar-upload">
            <div class="avatar-preview" v-if="websiteForm.pc_logo">
              <el-image :src="websiteForm.pc_logo" class="avatar-image" :preview-src-list="[websiteForm.pc_logo]"
                fit="cover" />
              <div class="avatar-actions">
                <el-button type="danger" size="small" @click="removeImage('pc_logo')">
                  <el-icon>
                    <Delete />
                  </el-icon>
                  删除
                </el-button>
              </div>
            </div>
            <input type="file" ref="logoInput" style="display: none" accept="image/*"
              @change="(e) => handleImageChange(e, 'pc_logo')" />
            <el-button type="primary" @click="$refs.logoInput.click()">
              <el-icon>
                <Upload />
              </el-icon>
              {{ websiteForm.pc_logo ? '更换LOGO' : '上传LOGO' }}
            </el-button>
            <div class="upload-tip">建议尺寸：200×50像素，支持jpg、png格式，大小不超过20MB</div>
          </div>
        </el-form-item>

        <el-form-item label="网站图标">
          <div class="avatar-upload">
            <div class="avatar-preview" v-if="websiteForm.pc_ico">
              <el-image :src="websiteForm.pc_ico" class="avatar-image" :preview-src-list="[websiteForm.pc_ico]"
                fit="cover" />
              <div class="avatar-actions">
                <el-button type="danger" size="small" @click="removeImage('pc_ico')">
                  <el-icon>
                    <Delete />
                  </el-icon>
                  删除
                </el-button>
              </div>
            </div>
            <input type="file" ref="icoInput" style="display: none" accept="image/*"
              @change="(e) => handleImageChange(e, 'pc_ico')" />
            <el-button type="primary" @click="$refs.icoInput.click()">
              <el-icon>
                <Upload />
              </el-icon>
              {{ websiteForm.pc_ico ? '更换图标' : '上传图标' }}
            </el-button>
            <div class="upload-tip">建议尺寸：100×100像素，支持ico、png格式，大小不超过20MB</div>
          </div>
        </el-form-item>

        <el-form-item label="网站背景">
          <div class="avatar-upload">
            <div class="avatar-preview" v-if="websiteForm.pc_bg">
              <el-image :src="websiteForm.pc_bg" class="avatar-image" :preview-src-list="[websiteForm.pc_bg]"
                fit="cover" />
              <div class="avatar-actions">
                <el-button type="danger" size="small" @click="removeImage('pc_bg')">
                  <el-icon>
                    <Delete />
                  </el-icon>
                  删除
                </el-button>
              </div>
            </div>
            <input type="file" ref="bgInput" style="display: none" accept="image/*"
              @change="(e) => handleImageChange(e, 'pc_bg')" />
            <el-button type="primary" @click="$refs.bgInput.click()">
              <el-icon>
                <Upload />
              </el-icon>
              {{ websiteForm.pc_bg ? '更换背景' : '上传背景' }}
            </el-button>
            <div class="upload-tip">建议尺寸：1920×1080像素，支持jpg、png格式，大小不超过20MB</div>
          </div>
        </el-form-item>

        <!-- 文本输入部分 -->
        <el-form-item label="网站标题">
          <el-input v-model="websiteForm.pc_title" placeholder="请输入网站标题" maxlength="50" show-word-limit />
        </el-form-item>

        <el-form-item label="网站描述">
          <el-input v-model="websiteForm.pc_desc" type="textarea" :rows="3" placeholder="请输入网站描述" maxlength="200"
            show-word-limit />
        </el-form-item>

        <el-form-item label="网站关键词">
          <el-input v-model="websiteForm.pc_keywords" placeholder="请输入网站关键词，多个关键词用逗号分隔" />
        </el-form-item>

        <!-- 网站设置弹窗中的跳转路径字段 -->
        <el-form-item label="跳转路径">
          <el-input v-model="websiteForm.pc_tz_path" placeholder="跳转路径将自动生成" readonly />
          <div class="field-tip">跳转路径自动生成，不可修改</div>
        </el-form-item>

        <el-form-item label="联系电话">
          <el-input v-model="websiteForm.pc_contact_tel" placeholder="请输入联系电话" />
        </el-form-item>

        <el-form-item label="联系邮箱">
          <el-input v-model="websiteForm.pc_contact_mail" placeholder="请输入联系邮箱" />
        </el-form-item>

        <el-form-item label="媒体账号">
          <el-input v-model="websiteForm.pc_contact_mt" placeholder="请输入媒体账号" />
        </el-form-item>

        <el-form-item label="版权信息">
          <el-input v-model="websiteForm.pc_copyright" type="textarea" :rows="2" placeholder="请输入版权信息" maxlength="500"
            show-word-limit />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="websiteDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveWebsiteSettings" :loading="loading.website">
            保存设置
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 二维码大图预览弹窗 -->
    <el-dialog v-model="qrcodePreviewVisible" title="小程序码预览" width="500px" align-center>
      <div class="qrcode-preview-container">
        <img :src="miniprogramForm.qrcode_url" alt="小程序码" class="preview-image" />
        <div class="preview-actions">
          <el-button type="primary" @click="downloadQrcode" size="large">
            <el-icon>
              <Download />
            </el-icon>
            下载二维码
          </el-button>
          <el-button @click="copyToClipboard(miniprogramForm.qrcode_url)" size="large">
            <el-icon>
              <DocumentCopy />
            </el-icon>
            复制链接
          </el-button>
        </div>
        <div class="preview-tip">
          <el-icon>
            <InfoFilled />
          </el-icon>
          此二维码永久有效，扫描可直接进入小程序
        </div>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';
import {
  Platform, Refresh, Iphone, Check, Upload, Picture, DocumentCopy,
  ChatDotRound, Monitor, Clock, InfoFilled, Setting, Delete,
  Download, View, SuccessFilled, DataAnalysis
} from '@element-plus/icons-vue';

export default {
  name: 'PlatformManagement',
  components: {
    Platform, Refresh, Iphone, Check, Upload, Picture, DocumentCopy,
    ChatDotRound, Monitor, Clock, InfoFilled, Setting, Delete,
    Download, View, SuccessFilled, DataAnalysis
  },
  data() {
    return {
      activeTab: 'miniprogram', // 默认激活的标签页
      loading: {
        miniprogram: false,
        offiaccount: false,
        h5: false,
        pc: false,
        qrcode: false,
        refresh: false,
        upload: false,
        website: false,
        miniQrcode: false,
        forceGenerate: false
      },
      miniprogramForm: {
        appid: '',
        secret: '',
        private_key: '',
        access_token: '',
        token_expires_in: null,
        token_get_time: null,
        qrcode_filename: '',
        qrcode_url: '',
        qrcode_last_generate_time: null,
        qrcode_generate_count: 0,
        enable_phone_auth: 0,
        last_update_time: null
      },
      // 二维码预览
      qrcodePreviewVisible: false,
      offiaccountForm: {
        appid: '',
        secret: '',
        callback_url: '',
        enable_phone_auth: 0,
        last_update_time: null
      },
      h5Form: {
        enable_phone_auth: 0,
        last_update_time: null
      },
      pcForm: {
        enable_phone_auth: 0,
        last_update_time: null
      },
      accessUrls: {
        miniprogram: '',
        offiaccount: '',
        h5: '',
        pc: ''
      },
      offiaccountQrcode: '',

      // 网站设置相关
      websiteDialogVisible: false,
      websiteForm: {
        pc_logo: '',
        pc_title: '',
        pc_ico: '',
        pc_bg: '',
        pc_desc: '',
        pc_keywords: '',
        pc_tz_path: '',
        pc_contact_tel: '',
        pc_contact_mail: '',
        pc_contact_mt: '',
        pc_copyright: ''
      }
    };
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}');
    }
  },
  mounted() {
    this.fetchAllSettings();
  },
  methods: {
    // 获取所有设置
    async fetchAllSettings() {
      this.loading.refresh = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'get_all_settings',
            store_id: this.userInfo.id
          }
        );

        if (response.data.data) {
          const data = response.data.data;

          // 如果有PC网站设置数据，填充到表单
          if (data.pc_website) {
            this.websiteForm = { ...this.websiteForm, ...data.pc_website };
            // 确保跳转路径正确
            if (!this.websiteForm.pc_tz_path) {
              this.websiteForm.pc_tz_path = `${config.BASE_URL}/pc/?store_id=${this.userInfo.id}`;
            }
          } else {
            // 如果没有数据，设置默认跳转路径
            this.websiteForm.pc_tz_path = `${config.BASE_URL}/pc/?store_id=${this.userInfo.id}`;
          }

          this.miniprogramForm = data.miniprogram;
          this.offiaccountForm = data.offiaccount;
          this.h5Form = data.h5;
          this.pcForm = data.pc;
          this.accessUrls = data.access_urls;

          ElMessage.success('数据刷新成功');
        }
      } catch (error) {
        ElMessage.error('获取平台设置失败');
        console.error('获取平台设置错误:', error);
      } finally {
        this.loading.refresh = false;
      }
    },

    // 获取小程序二维码
    async getMiniprogramQrcode(force = false) {
      if (!this.miniprogramForm.appid || !this.miniprogramForm.secret) {
        ElMessage.warning('请先配置小程序AppID和密钥');
        return;
      }

      const loadingKey = force ? 'forceGenerate' : 'miniQrcode';
      this.loading[loadingKey] = true;

      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'generate_miniprogram_qrcode',
            store_id: this.userInfo.id,
            force: force
          }
        );

        if (response.data.success) {
          // 更新小程序码数据
          this.miniprogramForm.qrcode_url = response.data.qrcode_url;
          this.miniprogramForm.qrcode_filename = response.data.qrcode_filename;
          this.miniprogramForm.qrcode_last_generate_time = response.data.generate_time;
          this.miniprogramForm.qrcode_generate_count = response.data.generate_count;

          const message = response.data.is_cached ?
            '已加载缓存的小程序码' :
            '小程序码生成成功！';
          ElMessage.success(message + (force ? ' (强制重新生成)' : ''));

        } else {
          let errorMsg = response.data.error || '获取失败';
          if (errorMsg.includes('已达上限')) {
            errorMsg += '，二维码总数已达10万个限制';
          }
          ElMessage.error(errorMsg);
        }
      } catch (error) {
        console.error('获取小程序码错误详情:', error);

        let errorMsg = '获取小程序码失败';
        if (error.response && error.response.data) {
          errorMsg = error.response.data.error || errorMsg;
        } else if (error.message) {
          errorMsg = error.message;
        }

        ElMessage.error(errorMsg);
      } finally {
        this.loading[loadingKey] = false;
      }
    },

    // 处理二维码图片加载错误
    handleQrcodeError(event) {
      console.error('二维码图片加载失败:', event);
      ElMessage.warning('二维码图片加载失败，请重新生成');
      // 可以显示一个占位符
      event.target.style.display = 'none';
      event.target.parentElement.innerHTML = `
        <div class="qrcode-error">
          <el-icon><Picture /></el-icon>
          <p>图片加载失败</p>
          <el-button size="mini" @click="getMiniprogramQrcode(true)">重新生成</el-button>
        </div>
      `;
    },

    // 查看二维码大图
    viewQrcode() {
      if (!this.miniprogramForm.qrcode_url) {
        ElMessage.warning('请先生成小程序码');
        return;
      }
      this.qrcodePreviewVisible = true;
    },

    // 下载二维码
    async downloadQrcode() {
      if (!this.miniprogramForm.qrcode_url) {
        ElMessage.warning('没有可下载的二维码');
        return;
      }

      try {
        // 创建一个隐藏的a标签进行下载
        const link = document.createElement('a');
        link.href = this.miniprogramForm.qrcode_url;
        link.download = `miniprogram_qrcode_${this.userInfo.id}_${Date.now()}.png`;
        link.target = '_blank';

        // 添加到DOM中
        document.body.appendChild(link);
        link.click();

        // 清理
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }, 100);

        ElMessage.success('二维码下载成功');
      } catch (error) {
        console.error('下载二维码错误:', error);
        ElMessage.error('下载失败，请重试');
      }
    },

    // 格式化时间
    formatTime(timeString) {
      if (!timeString) return '';
      const date = new Date(timeString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // 保存小程序设置（新增这个方法）
    async saveMiniprogram() {
      if (!this.miniprogramForm.appid || !this.miniprogramForm.secret) {
        ElMessage.warning('请填写小程序AppID和密钥');
        return;
      }

      this.loading.miniprogram = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_miniprogram',
            store_id: this.userInfo.id,
            ...this.miniprogramForm
          }
        );

        if (response.data.success) {
          ElMessage.success('小程序设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存小程序设置失败');
        console.error('保存小程序设置错误:', error);
      } finally {
        this.loading.miniprogram = false;
      }
    },


    // 保存公众号设置
    async saveOffiaccount() {
      if (!this.offiaccountForm.appid || !this.offiaccountForm.secret) {
        ElMessage.warning('请填写公众号AppID和密钥');
        return;
      }

      this.loading.offiaccount = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_offiaccount',
            store_id: this.userInfo.id,
            ...this.offiaccountForm
          }
        );

        if (response.data.success) {
          ElMessage.success('公众号设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存公众号设置失败');
        console.error('保存公众号设置错误:', error);
      } finally {
        this.loading.offiaccount = false;
      }
    },

    // 保存H5设置
    async saveH5() {
      this.loading.h5 = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_h5',
            store_id: this.userInfo.id,
            enable_phone_auth: this.h5Form.enable_phone_auth
          }
        );

        if (response.data.success) {
          ElMessage.success('H5设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存H5设置失败');
        console.error('保存H5设置错误:', error);
      } finally {
        this.loading.h5 = false;
      }
    },

    // 保存PC设置
    async savePC() {
      this.loading.pc = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_pc',
            store_id: this.userInfo.id,
            enable_phone_auth: this.pcForm.enable_phone_auth
          }
        );

        if (response.data.success) {
          ElMessage.success('PC设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存PC设置失败');
        console.error('保存PC设置错误:', error);
      } finally {
        this.loading.pc = false;
      }
    },

    // 打开网站设置弹窗
    openWebsiteSettings() {
      // 自动生成跳转路径
      this.websiteForm.pc_tz_path = `${config.BASE_URL}/pc/?store_id=${this.userInfo.id}`;

      // 滚动到页面顶部
      window.scrollTo({
        top: 0,
        behavior: 'smooth' // 平滑滚动
      });

      // 短暂延迟确保滚动完成后再打开弹窗
      setTimeout(() => {
        this.websiteDialogVisible = true;
      }, 100);
    },

    // 保存网站设置
    async saveWebsiteSettings() {
      this.loading.website = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_pc_website',
            store_id: this.userInfo.id,
            ...this.websiteForm
          }
        );

        if (response.data.success) {
          ElMessage.success('网站设置保存成功');
          this.websiteDialogVisible = false;
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存网站设置失败');
        console.error('保存网站设置错误:', error);
      } finally {
        this.loading.website = false;
      }
    },

    // 处理图片选择
    async handleImageChange(event, field) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // 文件验证
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
        const maxSize = 20 * 1024 * 1024; // 20MB

        if (!allowedTypes.includes(file.type)) {
          ElMessage.error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
          return;
        }

        if (file.size > maxSize) {
          ElMessage.error(`文件大小超过限制 (20MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
          return;
        }

        ElMessage.info('图片上传中，请稍候...');
        const url = await this.uploadImage(file);
        this.websiteForm[field] = url;
        ElMessage.success('图片上传成功');

        // 清空input，允许重复选择同一文件
        event.target.value = '';
      } catch (error) {
        ElMessage.error(`图片上传失败: ${error.message}`);
        console.error('图片上传错误:', error);
      }
    },

    // 上传图片到OSS
    async uploadImage(file) {
      try {
        const loading = this.$loading({
          lock: true,
          text: '图片上传中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });

        const signature = await getUploadSignature(file.type.split('/')[0]);
        const res = await uploadToOSS(file, signature);

        loading.close();
        return res.url;
      } catch (error) {
        this.$loading().close();
        console.error('图片上传失败:', error);
        throw error;
      }
    },

    // 删除图片
    removeImage(field) {
      this.websiteForm[field] = '';
    },

    // 上传小程序
    async uploadMiniprogram() {
      if (!this.miniprogramForm.appid) {
        ElMessage.warning('请先配置小程序AppID');
        return;
      }

      if (!this.miniprogramForm.private_key) {
        ElMessage.warning('请先配置小程序上传密钥');
        return;
      }

      try {
        await ElMessageBox.confirm(
          '确定要上传小程序代码吗？上传过程可能需要几分钟，请勿关闭页面。',
          '确认上传',
          {
            confirmButtonText: '确定上传',
            cancelButtonText: '取消',
            type: 'warning',
          }
        );

        this.loading.upload = true;
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'upload_miniprogram',
            store_id: this.userInfo.id
          }
        );

        // 处理不同的响应格式
        if (response.data.success || response.data.status === 'success') {
          ElMessage.success('小程序上传成功！');
        } else if (response.data.error) {
          ElMessage.error(response.data.error);
        } else {
          ElMessage.error('上传失败，请检查配置信息');
        }
      } catch (error) {
        if (error !== 'cancel') {
          // 处理不同的错误格式
          const errorMsg = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            '上传失败';
          ElMessage.error('上传小程序失败：' + errorMsg);
          console.error('上传小程序错误:', error);
        }
      } finally {
        this.loading.upload = false;
      }
    },

    // 生成公众号二维码
    async generateOffiaccountQrcode() {
      this.loading.qrcode = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'generate_offiaccount_qrcode',
            store_id: this.userInfo.id
          }
        );

        if (response.data.success) {
          this.offiaccountQrcode = response.data.qrcode_url;
          ElMessage.success('公众号二维码生成成功');
        } else {
          ElMessage.error(response.data.error || '生成失败');
        }
      } catch (error) {
        ElMessage.error('生成公众号二维码失败');
        console.error('生成公众号二维码错误:', error);
      } finally {
        this.loading.qrcode = false;
      }
    },

    // 复制到剪贴板
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        ElMessage.success('已复制到剪贴板');
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        ElMessage.success('已复制到剪贴板');
      }
    }
  }
};
</script>

<style scoped lang="scss">
.platform-management {
  padding: 20px;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 0 8px;

  // 默认无背景样式
  &.with-bg {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 30px;
    border-radius: 12px;
    margin: 0 0 24px 0;
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);

    .header-content {
      .page-title {
        color: white;
        margin-bottom: 12px;

        .title-icon {
          color: rgba(255, 255, 255, 0.9);
        }
      }

      .page-desc {
        color: rgba(255, 255, 255, 0.85);
        font-size: 15px;
        margin: 0;
      }
    }

    .refresh-btn {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: white;
      backdrop-filter: blur(10px);

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: translateY(-1px);
      }
    }
  }
}

.header-content {
  .page-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 24px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 8px;

    .title-icon {
      font-size: 28px;
      color: #409eff;
    }
  }

  .page-desc {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
  }
}

.platform-tabs {
  background: transparent;
  border: none;

  :deep(.el-tabs__header) {
    background: #fff;
    border-radius: 8px 8px 0 0;
    margin-bottom: 0;
  }

  :deep(.el-tabs__nav-wrap) {
    border-radius: 8px 8px 0 0;
  }

  :deep(.el-tabs__item) {
    font-weight: 500;
    padding: 0 24px;
    height: 48px;
    line-height: 48px;

    &.is-active {
      color: #409eff;
      font-weight: 600;
    }
  }
}

.tab-card {
  border-radius: 0 0 8px 8px;
  border: 1px solid #e4e7ed;
  border-top: none;

  :deep(.el-card__header) {
    padding: 20px;
    border-bottom: 1px solid #e4e7ed;
    background: #f8fafc;
  }

  :deep(.el-card__body) {
    padding: 0;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.platform-info {
  display: flex;
  align-items: center;
  gap: 12px;

  .platform-icon {
    font-size: 20px;

    &.mini-icon {
      color: #67c23a;
    }

    &.offi-icon {
      color: #e6a23c;
    }

    &.h5-icon {
      color: #409eff;
    }

    &.pc-icon {
      color: #f56c6c;
    }
  }

  .platform-name {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }
}

.card-actions {
  display: flex;
  gap: 12px;
}

.form-container {
  padding: 24px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
}

:deep(.el-input) {
  .el-input-group__append {
    background: #409eff;
    border: none;
    color: white;

    .el-button {
      color: white;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }
}

.field-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
  margin-top: 6px;

  .el-icon {
    color: #e6a23c;
  }
}

.switch-label {
  margin-left: 8px;
  color: #606266;
  font-size: 14px;
}

.update-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909399;
  font-size: 14px;

  .el-icon {
    color: #909399;
  }
}

/* 样式更新 */
.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  max-width: 500px;
}

.qrcode-image-wrapper {
  position: relative;
  width: 250px;
  height: 250px;
  margin-bottom: 15px;
}

.qrcode-image {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid #ddd;
  object-fit: contain;
}

.qrcode-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
  color: #f56c6c;

  .el-icon {
    font-size: 40px;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    margin-bottom: 10px;
  }
}

.qrcode-info {
  width: 100%;
}

.qrcode-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
  padding: 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #606266;

  .el-icon {
    color: #909399;
    font-size: 14px;
  }
}

.qrcode-tip {
  text-align: center;
  color: #606266;
  font-size: 14px;
  margin: 10px 0;
}

.qrcode-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

/* 二维码预览样式 */
.qrcode-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.preview-image {
  width: 300px;
  height: 300px;
  border-radius: 8px;
  border: 2px solid #ebeef5;
  margin-bottom: 20px;
}

.preview-actions {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.preview-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #409eff;
  font-size: 14px;
  padding: 10px;
  background: #f0f7ff;
  border-radius: 6px;
  border: 1px solid #d9ecff;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .qrcode-image-wrapper {
    width: 200px;
    height: 200px;
  }

  .preview-image {
    width: 250px;
    height: 250px;
  }

  .preview-actions {
    flex-direction: column;
    width: 100%;

    .el-button {
      width: 100%;
    }
  }
}

/* 图片上传样式 */
.avatar-upload {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.avatar-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.avatar-image {
  width: 120px;
  height: 120px;
  border-radius: 6px;
  border: 2px solid #ebeef5;
  overflow: hidden;
}

.avatar-actions {
  display: flex;
  justify-content: center;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .card-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .platform-tabs {
    :deep(.el-tabs__item) {
      padding: 0 12px;
    }
  }

  .el-tabs--border-card>.el-tabs__content {
    padding: 20px;
  }

  .form-container {
    padding: 16px;
  }

  .avatar-upload {
    flex-direction: column;
    align-items: center;
  }

  :deep(.el-col-12) {
    width: 100%;
  }
}

/* 在小程序码样式部分 */
.qrcode-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
  color: #909399;

  .el-icon {
    font-size: 40px;
    margin-bottom: 10px;
    color: #c0c4cc;
  }

  p {
    font-size: 12px;
    text-align: center;
    margin: 0;
    padding: 0 10px;
  }
}

.qrcode-info {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
}
</style>